/**
 * FoodFikaSection – lightweight editorial section for Mat & fika.
 * Gated on the `food_fika` offering.
 * Reuses RichTextEditor + serving-type chips.
 *
 * Participates in the parent editor's dirty-state via value/onChange props.
 * Does NOT manage its own persistence — the parent form's Save handles that.
 */

import { useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { SERVING_TYPES } from '@/lib/producer/foodFika';
import { cn } from '@/lib/utils';

interface FoodFikaSectionProps {
  description: string;
  servingTypes: string[];
  onDescriptionChange: (html: string) => void;
  onServingTypesChange: (types: string[]) => void;
}

export function FoodFikaSection({
  description,
  servingTypes,
  onDescriptionChange,
  onServingTypesChange,
}: FoodFikaSectionProps) {
  const handleToggleType = useCallback((key: string) => {
    // Immutable array update — parent receives a fresh reference
    const next = servingTypes.includes(key)
      ? servingTypes.filter((k) => k !== key)
      : [...servingTypes, key];
    onServingTypesChange(next);
  }, [servingTypes, onServingTypesChange]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Berätta om vad ni serverar här — till exempel fika, kaffe, lunch eller annan matupplevelse.
      </p>

      {/* Serving type chips */}
      <div className="space-y-1.5">
        <Label className="text-sm">Typ av servering</Label>
        <div className="flex flex-wrap gap-1.5">
          {SERVING_TYPES.map((type) => {
            const active = servingTypes.includes(type.key);
            return (
              <Badge
                key={type.key}
                variant={active ? 'default' : 'outline'}
                className={cn(
                  'cursor-pointer select-none transition-colors text-xs',
                  active
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'hover:bg-accent hover:text-accent-foreground',
                )}
                onClick={() => handleToggleType(type.key)}
              >
                {type.label}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Rich text description */}
      <div className="space-y-1.5">
        <Label className="text-sm">Beskrivning</Label>
        <RichTextEditor
          value={description}
          onChange={onDescriptionChange}
          placeholder="Berätta om ert café, meny, stämning, eller varför gäster bör stanna för en fika…"
          preset="simple"
          expandable
          expandedTitle="Mat & fika"
          expandedSurface="producer_editor"
          expandedField="food_fika"
        />
      </div>
    </div>
  );
}
